import { AssetDto, GalleryColorLabel, Section } from '@nusign/picflow-backend-types';
import { assetStatusOrderFlipped } from '../../gallery/pipes/asset-status-order.pipe';
import { randomString } from '../../lib/random-string';
import { removeHtml } from '../../storyblok/custom-render-text';
import { AssetGroup } from '../types/asset-group.type';

export const SortAssets = (
  assets: AssetDto[],
  sortingAndGrouping: string,
  sections: Section[],
  colorLabels: GalleryColorLabel[]
) => {
  const groups = getAssetGroups(assets, sortingAndGrouping, sections, colorLabels);
  const sortedAssets = [];
  groups.forEach(group => sortedAssets.push(...group.assets));
  return sortedAssets;
};

export const allowedAssetSortingAndGroupingValues = ['likes', 'status', 'color_label', 'auto'];

const fitsInGroup = (group: AssetGroup, asset: AssetDto, sortingAndGrouping: string) => {
  if (sortingAndGrouping === 'likes') {
    const gLikes = !!group.assets[0]['likes'];
    const iLikes = !!asset['likes'];
    return gLikes === iLikes;
  }

  sortingAndGrouping = sortingAndGrouping === 'auto' ? 'section' : sortingAndGrouping;
  return group.assets[0][sortingAndGrouping] === asset[sortingAndGrouping];
};

export const getAssetGroups = (
  assets: AssetDto[],
  sortingAndGrouping: string,
  sections: Section[],
  colorLabels: GalleryColorLabel[]
): AssetGroup[] => {
  if (!allowedAssetSortingAndGroupingValues.includes(sortingAndGrouping))
    return [{ assets, id: assets[0]?.id || randomString(12) }];
  const assetGroups: AssetGroup[] = [];

  assets.forEach(img => {
    const group = assetGroups.find(g => fitsInGroup(g, img, sortingAndGrouping));
    if (group) group.assets.push(img);
    else {
      const newGroup: AssetGroup = { assets: [img], id: img.id };
      if (sortingAndGrouping === 'color_label') {
        newGroup.label = colorLabels.find(l => l.color === img.color_label);
        if (!newGroup.label) newGroup.label = null;
      }
      if (sortingAndGrouping === 'status') newGroup.status = img.status;
      if (sortingAndGrouping === 'likes') newGroup.likes = img['likes'];
      if (sortingAndGrouping === 'auto') {
        const section = sections.find(s => s.id === img.section);
        if (section) {
          const title = section.text_title ? removeHtml(section.text_title).replace('__EMPTY_TITLE__', '') : null;
          if (title) newGroup.title = title;
        }
      }
      assetGroups.push(newGroup);
    }
  });

  if (sortingAndGrouping === 'auto') {
    return sections
      .filter(s => s.object === 'asset')
      .map(section => {
        const group = assetGroups.find(g => g.assets[0].section === section.id);
        if (group) return group;
        return { assets: [], id: randomString(12) };
      });
  } else if (sortingAndGrouping === 'likes') {
    const isLiked = assetGroups.find(group => group.assets[0]['likes'] > 0);
    const notLiked = assetGroups.find(group => group.assets[0]['likes'] === 0);
    return [isLiked, notLiked].filter(a => a);
  } else if (sortingAndGrouping === 'status') {
    return assetStatusOrderFlipped
      .map(status => {
        return assetGroups.find(group => group.assets[0].status === status);
      })
      .filter(Boolean);
  } else if (sortingAndGrouping === 'color_label') {
    const none = assetGroups.find(group => !group.assets[0].color_label);
    const red = assetGroups.find(group => group.assets[0].color_label === 'red');
    const orange = assetGroups.find(group => group.assets[0].color_label === 'orange');
    const blue = assetGroups.find(group => group.assets[0].color_label === 'blue');
    const purple = assetGroups.find(group => group.assets[0].color_label === 'purple');
    const green = assetGroups.find(group => group.assets[0].color_label === 'green');
    return [red, orange, blue, purple, green, none].filter(a => a);
  }
};
